import React, { forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import { Formik, Form, Field, FieldProps } from 'formik';
import { TextField, Grid, Autocomplete, createFilterOptions } from '@mui/material';
import { BasicProjectInfoDTO } from '../project.type'; // Import your type
import * as Yup from 'yup'; // Import Yup
import { capitalize, compareCaseInsensitive, isPostalCodeValid } from 'common/utils/common.util';
import useLookups from '../../../hooks/useLookups';
import { LOOKUP_KEYS } from '../../../common/constants/lookups';
import { City, State } from '../../../components/lookups/lookups.type';
import { getCityString, getStateLabel, isValidCityState } from '../../../components/lookups/lookups.util';
import { validateCityState } from '../../../components/lookups/lookups.api';

// Falls back to a Google Geocode check when the local Cities lookup misses.
const isValidCityStateWithFallback = async (cityOptions: City[], city?: string | null, state?: string | null) => {
  if (isValidCityState(cityOptions, city, state)) return true;
  if (!city?.trim() || !state?.trim()) return false;
  try {
    const result = await validateCityState(city, state);
    return !!result?.isValid;
  } catch {
    return true; // service unavailable — don't block the user on a possibly-real address
  }
};

interface Props {
  projectData: BasicProjectInfoDTO;
  onUpdate: (data: BasicProjectInfoDTO) => void;
  onValidationChange: (isValid: boolean) => void;
}

export interface ProjectInfoRef {
  submitForm: () => void;
}

export const ProjectInfo = forwardRef<ProjectInfoRef, Props>(({ projectData, onUpdate, onValidationChange }, ref) => {
  const [postalCodeError, setPostalCodeError] = useState('');
  const stateOptions = useLookups(LOOKUP_KEYS.States) as State[];
  const cityOptions = useLookups(LOOKUP_KEYS.Cities) as City[];
  const cityOptionStrings = cityOptions.map(getCityString);
  const [selectedState, setSelectedState] = useState(projectData?.projectAddress?.state || '');
  const [citySelectedFromList, setCitySelectedFromList] = useState(false);

  const validationSchema = Yup.object().shape({
    projectNumber: Yup.string().required('Project Number is required'),
    projectTitle: Yup.string().required('Project Title is required'),
    projectAddress: Yup.object().shape({
      street: Yup.string(),
      city: Yup.string(),
      state: Yup.string().test('city-state-combo', 'This city/state combination does not exist', (value, context) =>
        isValidCityStateWithFallback(cityOptions, context.parent.city, value),
      ),
      postalCode: Yup.string()
        .matches(/^(?:\d{5}(?:-\d{4})?)?$/, 'Invalid postal code')
        .notRequired(), // Optional field, so use notRequired() if needed
    }),
  });

  // Sync selectedState with projectData when it changes
  useEffect(() => {
    if (projectData?.projectAddress?.state) {
      setSelectedState(projectData.projectAddress.state);
    }
  }, [projectData?.projectAddress?.state]);

  const handlePostalCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    formikRef.current.setFieldValue('projectAddress.postalCode', value);
    if (isPostalCodeValid(value)) {
      setPostalCodeError('');
    } else {
      setPostalCodeError('Invalid Postal Code. Please enter a valid postal code in the format 12345 or 12345-6789');
    }
    // Manually validate the form
    validateForm({
      ...formikRef.current?.values,
      projectAddress: {
        ...formikRef.current?.values.projectAddress,
        postalCode: value,
      },
    });
  };

  const formikRef = React.useRef<any>();

  // Function to validate the form and update parent component
  const validateForm = async (values: any) => {
    try {
      await validationSchema.validate(values, { abortEarly: false });
      onValidationChange(true);
    } catch (error) {
      onValidationChange(false);
    }
  };
  // Expose the submitForm method of Formik to the parent component
  useImperativeHandle(ref, () => ({
    submitForm: () => {
      if (formikRef.current) {
        formikRef.current.submitForm();
      }
    },
  }));

  return (
    <Formik
      innerRef={formikRef}
      initialValues={projectData}
      validationSchema={validationSchema}
      validateOnChange={false} // state field's test hits Google Geocode; validate on blur/submit only
      onSubmit={(values, actions) => {
        onUpdate(values);
        actions.setSubmitting(false);
      }}
      validate={validateForm}
    >
      <Form noValidate autoComplete='off'>
        <Grid container spacing={2} style={{ maxWidth: '500px', margin: 'auto' }}>
          <Grid size={{ xs: 12 }}>
            <Field as={TextField} fullWidth label='Project Number' name='projectNumber' variant='outlined' margin='normal' required />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Field as={TextField} fullWidth label='Project Title' name='projectTitle' variant='outlined' margin='normal' required />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Field as={TextField} fullWidth label='Street' name='projectAddress.street' variant='outlined' margin='normal' />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Field name='projectAddress.city'>
              {({ field, form }: FieldProps) => (
                <Autocomplete
                  {...field}
                  id='project-address-city-autocomplete'
                  freeSolo
                  forcePopupIcon={false}
                  options={cityOptionStrings}
                  getOptionLabel={option => (typeof option === 'string' ? option : option)}
                  autoComplete
                  autoHighlight
                  filterOptions={createFilterOptions({ limit: 10, matchFrom: 'start' })}
                  onInputChange={(_, value) => {
                    form.setFieldValue(field.name, value ?? '');
                    setCitySelectedFromList(false);
                  }}
                  onChange={(event, newValue) => {
                    const isOptionFromList = newValue && typeof newValue === 'string' && cityOptionStrings.includes(newValue);
                    if (isOptionFromList && newValue) {
                      const parts = newValue.split(', ');
                      const city = parts[0] ?? '';
                      const stateCode = parts[1] ?? '';
                      form.setFieldValue(field.name, city);
                      const matchingState = stateOptions.find(option => compareCaseInsensitive(option.stateCode, stateCode));
                      const stateName = capitalize(matchingState?.name) ?? '';
                      setSelectedState(stateName);
                      form.setFieldValue('projectAddress.state', stateName);
                      setCitySelectedFromList(true);
                    } else {
                      setCitySelectedFromList(false);
                      if (newValue !== null) {
                        form.setFieldValue(field.name, typeof newValue === 'string' ? newValue : '');
                      }
                      if (!newValue) setSelectedState('');
                    }
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      margin='normal'
                      label='City'
                      sx={{
                        '& .MuiInputBase-input': {
                          height: '10px',
                          fontSize: '0.875rem',
                          padding: '0px 0px',
                        },
                      }}
                    />
                  )}
                />
              )}
            </Field>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Field name='projectAddress.state'>
              {({ field, form }: FieldProps) =>
                citySelectedFromList ? (
                  <TextField
                    {...field}
                    value={field.value || selectedState}
                    placeholder='State'
                    label='State'
                    type='text'
                    fullWidth
                    inputProps={{ readOnly: true }}
                    sx={{ pointerEvents: 'none' }}
                  />
                ) : (
                  <Autocomplete
                    freeSolo
                    options={stateOptions}
                    getOptionLabel={option => (typeof option === 'string' ? option : (getStateLabel(option) ?? ''))}
                    value={field.value || ''}
                    inputValue={field.value || ''}
                    onInputChange={(_, newInputValue, reason) => {
                      if (reason === 'input') {
                        form.setFieldValue(field.name, newInputValue, false);
                      }
                    }}
                    onChange={(_, newValue) => {
                      const stateName = typeof newValue === 'string' ? newValue : newValue ? (getStateLabel(newValue) ?? '') : '';
                      form.setFieldValue(field.name, stateName);
                    }}
                    filterOptions={createFilterOptions({ limit: 10, matchFrom: 'start' })}
                    renderInput={params => (
                      <TextField
                        {...params}
                        margin='normal'
                        label='State'
                        placeholder='State'
                        onBlur={() => {
                          form.setFieldTouched(field.name, true);
                        }}
                        error={Boolean((form.errors as any)?.projectAddress?.state)}
                        helperText={(form.errors as any)?.projectAddress?.state || ''}
                        sx={{
                          '& .MuiInputBase-input': { height: '10px', fontSize: '0.875rem', padding: '0px 0px' },
                        }}
                      />
                    )}
                  />
                )
              }
            </Field>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Field
              as={TextField}
              fullWidth
              label='Postal Code'
              name='projectAddress.postalCode'
              variant='outlined'
              margin='normal'
              onChange={handlePostalCodeChange}
              error={Boolean(postalCodeError)}
              helperText={postalCodeError}
            />
          </Grid>
        </Grid>
      </Form>
    </Formik>
  );
});
ProjectInfo.displayName = 'ProjectInfo';
